import { httpService } from "../httpService.service";

// Backend contract — TRACK 1 Workflow & dashboards (base /api/Dashboard).
// Thin wrappers over httpService so the rest of the app calls the new
// endpoints by name. Each returns the underlying promise and forwards the
// standard (callback, errCallback) used everywhere else in this app.

// F-01 — Role-based home. Returns { role, modules: [{ key, label, route }] }.
// The sidebar/nav is built dynamically from `modules` (render only what's
// returned; hide the rest, don't disable).
export const getHomeManifest = (callback, errCallback) =>
  httpService.get(`/api/Dashboard/homeManifest`, callback, errCallback);

// F-01 — PM home (SystemAdmin, ProjectManager). One aggregated call for the
// whole PM landing screen: { projectHealth: TPMSummaryResponse,
// ipCompleteness: IPCompletenessResponse[] }. projectHealth carries the status
// rollups plus stalledOverview and flagPatterns, so the PM gets a single
// dashboard instead of several disconnected panels.
export const getPmHome = (projectId, callback, errCallback) =>
  httpService.get(
    `/api/Dashboard/pmHome?projectId=${projectId}`,
    callback,
    errCallback
  );

// F-01 — Field Coordinator home (SystemAdmin, ProjectManager,
// FieldCoordinator). Returns { coordinatorId, totalVisits, pendingVisits,
// stalledVisits, visits: [{ siteVisitId, name, code, status, isStalled,
// plannedVisit, updatedDate }] } — the coordinator's own visit queue.
export const getCoordinatorHome = (projectId, callback, errCallback) =>
  httpService.get(
    `/api/Dashboard/coordinatorHome?projectId=${projectId}`,
    callback,
    errCallback
  );

// F-02 — Task Manager home. Returns
// List<{ task: TaskResponse, nextBestAction: string, isOverdue: bool }>,
// already priority-sorted (overdue first). TaskResponse carries the F-04
// siteVisitProgress: { completedTasks, totalTasks, percentage } | null and the
// stalled-escalation fields isStalled / stalledSince.
//
// Accepts either a plain projectId (legacy call sites) or an options object
// { projectId, overdueOnly?, status?, isStalled? } to use the server-side
// filters (OverdueOnly / Status / IsStalled).
export const getTaskManagerHome = (arg, callback, errCallback) => {
  const opts = typeof arg === "object" && arg !== null ? arg : { projectId: arg };
  const { projectId, overdueOnly, status, isStalled } = opts;
  const params = new URLSearchParams();
  if (projectId) params.append("ProjectId", projectId);
  if (overdueOnly) params.append("OverdueOnly", true);
  if (isStalled) params.append("IsStalled", true);
  if (status !== undefined && status !== null && status !== "")
    params.append("Status", status);
  return httpService.get(
    `/api/Tasks/taskManagerHome?${params.toString()}`,
    callback,
    errCallback
  );
};

// Automated Status Escalation (spec: Related Item = Tasks). Manual PM/Admin
// trigger that re-evaluates every active task's last-updated timestamp and
// flags the stalled ones (TaskResponse.isStalled / stalledSince). The stalled
// flag clears automatically once a task is updated again.
export const escalateStalledTasks = (callback, errCallback) =>
  httpService.post(`/api/Tasks/escalateStalledTasks`, {}, callback, errCallback);

// F-03 — stalled threshold configuration (SystemAdmin / ProjectManager).
// The escalation worker resolves the inactivity threshold in this order:
//   project.stalledThresholdDays → per-task-type override → default (5 days).
// These endpoints own the middle tier; the project override lives on the
// project create/update payloads (see NewProject / EditProject).

// GET /api/Tasks/stalledThresholds →
// [{ id, taskType, taskTypeLabel, thresholdDays }].
export const getStalledThresholds = (callback, errCallback) =>
  httpService.get(`/api/Tasks/stalledThresholds`, callback, errCallback);

// PUT /api/Tasks/stalledThresholds — upsert, keyed on taskType.
// Body { taskType, thresholdDays } (thresholdDays >= 1).
export const upsertStalledThreshold = (
  { taskType, thresholdDays } = {},
  callback,
  errCallback
) =>
  httpService.put(
    `/api/Tasks/stalledThresholds`,
    { taskType, thresholdDays },
    callback,
    errCallback
  );

// DELETE /api/Tasks/stalledThresholds?id= — drops the override so that task
// type falls back to the default again.
export const deleteStalledThreshold = (id, callback, errCallback) =>
  httpService.delete(
    `/api/Tasks/stalledThresholds?id=${id}`,
    null,
    callback,
    errCallback
  );

// F-06 — Calendar planning view. Returns
// List<{ id, type: "SiteVisit"|"Task"|"Logistic", title, date, projectId, siteVisitId? }>.
export const getCalendar = (
  { projectId, from, to } = {},
  callback,
  errCallback
) => {
  const params = new URLSearchParams();
  if (projectId) params.append("projectId", projectId);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  return httpService.get(
    `/api/Dashboard/calendar?${params.toString()}`,
    callback,
    errCallback
  );
};

// F-06 — Drag-to-reschedule a task. Body { taskId, newDeadline }.
export const rescheduleTask = (taskId, newDeadline, callback, errCallback) =>
  httpService.put(
    `/api/Dashboard/rescheduleTask`,
    { taskId, newDeadline },
    callback,
    errCallback
  );

// F-06 — Drag-to-reschedule a site visit. Body { siteVisitId, newPlannedDate }.
export const rescheduleSiteVisit = (
  siteVisitId,
  newPlannedDate,
  callback,
  errCallback
) =>
  httpService.put(
    `/api/Dashboard/rescheduleSiteVisit`,
    { siteVisitId, newPlannedDate },
    callback,
    errCallback
  );

// F-10 — Team leaderboard. Returns
// List<{ coordinatorId, coordinatorName, totalVisits, documentedVisits, documentationRate }>,
// best-first.
export const getTeamLeaderboard = (projectId, callback, errCallback) =>
  httpService.get(
    `/api/Dashboard/teamLeaderboard?projectId=${projectId}`,
    callback,
    errCallback
  );
